import { useCallback, useMemo } from 'react'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import getPermissionGrants from './api/entity/permission/getPermissionGrants'
import { PermissionGrantDTO } from '../domain/permission/PermissionGrantDTO'

export const PERMISSION_GRANTS_QUERY_KEY = ['permission-grants'] as const

const DEFAULT_PAGE_SIZE = 50

export type PermissionGrantsPage = { grants: PermissionGrantDTO[]; totalCount: number }
export type FetchPermissionGrantsPage = (params: {
  pageIndex: number
  pageSize: number
}) => Promise<PermissionGrantsPage>

const defaultFetchPage: FetchPermissionGrantsPage = async ({ pageIndex, pageSize }) => {
  const response = await getPermissionGrants({ pageIndex, pageSize })
  return {
    grants: response.documents.asJsReadonlyArrayView() as PermissionGrantDTO[],
    totalCount: response.totalCount,
  }
}

export type UsePaginatedPermissionGrantsResult = {
  grants: PermissionGrantDTO[]
  totalCount: number | undefined
  loadMore: () => void
  reload: () => void
  hasMore: boolean
  loading: boolean
}

type UsePaginatedPermissionGrantsParams = {
  pageSize?: number
  fetchPage?: FetchPermissionGrantsPage
  queryKeySuffix?: string
}

const usePaginatedPermissionGrants = ({
  pageSize = DEFAULT_PAGE_SIZE,
  fetchPage = defaultFetchPage,
  queryKeySuffix,
}: UsePaginatedPermissionGrantsParams = {}): UsePaginatedPermissionGrantsResult => {
  const query = useInfiniteQuery({
    queryKey: [...PERMISSION_GRANTS_QUERY_KEY, pageSize, queryKeySuffix ?? ''],
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const page = await fetchPage({ pageIndex: pageParam, pageSize })
      return {
        grants: page.grants,
        totalCount: page.totalCount,
        nextPage: pageParam + 1,
      }
    },
    getNextPageParam: (lastPage) =>
      lastPage.nextPage * pageSize < lastPage.totalCount ? lastPage.nextPage : undefined,
    initialPageParam: 0,
  })

  const grants = useMemo(() => query.data?.pages.flatMap((page) => page.grants) ?? [], [query.data?.pages])
  const totalCount = query.data?.pages[0]?.totalCount

  const loadMore = useCallback(() => {
    if (query.hasNextPage && !query.isFetchingNextPage) query.fetchNextPage()
  }, [query])

  const reload = useCallback(() => {
    query.refetch()
  }, [query])

  return {
    grants,
    totalCount,
    loadMore,
    reload,
    hasMore: query.hasNextPage ?? false,
    loading: query.isLoading || query.isFetchingNextPage || query.isRefetching,
  }
}

export const useInvalidatePermissionGrants = () => {
  const queryClient = useQueryClient()
  return useCallback(() => queryClient.invalidateQueries({ queryKey: PERMISSION_GRANTS_QUERY_KEY }), [queryClient])
}

export default usePaginatedPermissionGrants
